import { useEffect, useState } from "react";
import { Formik } from "formik";
import SelectDropdown from "../../components/form-control/SelectDropdown";
import Button from "../../components/form-control/Button";
import { CreateReconciliationBatch } from "../../api/requests";
import TextArea from "../../components/form-control/TextArea";
import { ICreateReconciliationDTO } from "../../resources/interfaces";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import * as Yup from "yup";
import useSearchQuery from "../../hooks/SearchQueryHook";
import { useQuery } from "@tanstack/react-query";
import DatePickerInput from "../../components/form-control/DatePicker/DatePickerInput";
import { SERVER_DATE_FORMAT } from "../../resources/constants";
import moment from "moment";
import useUsers from "../../hooks/UsersHook";
import CediSign from "../../components/elements/CediSign";
import ToastContent from "../../components/ToastContent";
import { Reconciliation } from "../../resources/types";

interface IProps {
    amountExpected: number;
    transactions: string[]
    onComplete: (reconciliation: Reconciliation) => void;
}

const SubmitReconciliation = (props: IProps): JSX.Element => {
    const { fetchUsers, NON_AGENT_USERS_LIST_FOR_FORM } = useUsers()
    const [submitting, setSubmitting] = useState<boolean>(false);
    const { debouncedQuery, setQuery } = useSearchQuery();


    const { data, isLoading, isStale, isFetching } = useQuery<any, Error>({
        queryKey: [
            'users',
            debouncedQuery,
        ],
        queryFn: () => fetchUsers({ query: debouncedQuery })
    });

    const initialValues = {
        received_by_user_id: "",
        received_at: new Date(),
        description: "",
        transactions: props.transactions
    }

    const submitHandler = (values: ICreateReconciliationFormData) => {
        const payload: ICreateReconciliationDTO = {
            ...values,
            received_at: moment(values.received_at)?.format(SERVER_DATE_FORMAT)
        }

        setSubmitting(true);
        CreateReconciliationBatch(payload)
            .then((response: any) => {

                if (response) {
                    props.onComplete(response);
                }
                toast.success(`${"Rconciliation batch submitted successfully."}`);
            }).catch((error: any | AxiosError | Error) => {
                toast.error(<ToastContent
                    title={error.description}
                    description={error.detail ?? error.message}

                />, { autoClose: 5000 });
            }).finally(() => setSubmitting(false))
    }

    return (
        <div>
            <div>
                <h3 className="mt-4 text-lg"><b>Amount Expected:</b> <CediSign /> {props.amountExpected} </h3>
                <span className="text-gray-500"> {props.transactions.length} Transactions</span>
            </div>
            <Formik
                initialValues={initialValues}
                validationSchema={Yup.object({
                    received_by_user_id: Yup.string()
                        .trim()
                        .required('Received by is required'),
                    received_at: Yup.string()
                        .trim()
                        .required('Date of submission required')
                })}
                onSubmit={(values: ICreateReconciliationFormData) => {
                    submitHandler(values);
                }}>
                {({ isSubmitting, values, handleBlur, handleChange, handleSubmit, setFieldValue, touched, errors }) => (
                    <form onSubmit={handleSubmit} className="mt-4">
                        <div className="flex flex-col gap-4">

                            <SelectDropdown
                                list={NON_AGENT_USERS_LIST_FOR_FORM}
                                listSelectedValue={values.received_by_user_id}
                                onChange={(value: string) => {
                                    setFieldValue("received_by_user_id", value);
                                }}
                                label="Received By"
                                wrapperClasses="w-full rounded"
                                placeholder="Select"
                                error={touched.received_by_user_id ? errors.received_by_user_id : undefined
                                }
                                helperText={touched?.received_by_user_id ? errors.received_by_user_id : undefined
                                }
                            />

                            <DatePickerInput
                                id="received_at"
                                selected={values.received_at}
                                onChange={(date: Date | null) => {
                                    setFieldValue("received_at", date);
                                }}
                                dateFormat="dd-MM-yyyy"
                                label="Date Submitted"
                                showYearDropdown
                                placeholder="Enter date submitted"
                                allowFutureSelections={false}
                                required
                            // error={touched.received_at && errors.received_at !== undefined ? errors.received_at : undefined}
                            // helperText={touched.received_at ? errors.received_at : undefined}
                            />

                            <TextArea
                                value={values.description}
                                name="description"
                                label="Description"
                                placeholder="Enter description"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.description ? errors.description : undefined
                                }
                                helperText={touched?.description ? errors.description : undefined
                                }
                                rows={2}
                            />

                        </div>

                        <div className="flex justify-end mt-8">
                            <Button
                                label="Submit"
                                type="submit"
                                onClick={handleSubmit}
                                disabled={submitting}
                                loading={submitting}
                                className="px-12 bg-secondary-main rounded hover:bg-white hover:border-secondary-main"
                            />
                        </div>
                    </form>
                )}
            </Formik>
        </div>
    )
}

export default SubmitReconciliation;

export interface ICreateReconciliationFormData {
    received_by_user_id: string,
    received_at: Date,
    description: string,
    transactions: string[]
}